import { FC, useMemo, useState } from "react"
import cx from "classnames"
import TextField from "@/components/TextField"
import { usePagination } from "@/hooks/usePagination"
import SearchIcon from "@/assets/search.svg"
import AddIcon from "@/components/icons/AddIcon"
import { RiDeleteBin6Line as BinIcon } from "react-icons/ri"
import Button from "@/components/Button"
import EmptyList from "@/components/EmptyList"
import AddEditGsuiteModal from "./features/AddEditGsuiteModal"
import GsuiteAccountsTable from "./features/GsuiteAccountsTable"
import ErrorMessage from "@/components/ErrorMessage"
import DeleteGsuiteModal from "./features/DeleteGsuiteModal"
import Loader from "@/components/Loader"
import { useGetGsuiteAccountsQuery } from "@/redux/services/gsuitesApi"
import type { Key } from "antd/es/table/interface"

type Props = {
  className?: string
}

const GsuitePage: FC<Props> = ({ className, ...rest }: Props) => {
  const {
    searchTerm,
    handleSearchTermChange,
    limit,
    skip,
    handlePageChange,
    handlePageSizeChange,
  } = usePagination()
  const {
    data: { items: gsuites, total } = { items: [], total: 0 },
    isLoading,
    isFetching,
    isError,
  } = useGetGsuiteAccountsQuery({ limit, skip })
  const [openAddModal, setOpenAddModal] = useState(false)
  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const handleOpenAddModal = () => setOpenAddModal(true)
  const [selectedRowsKeys, setSelectedRowsKeys] = useState<Key[]>([])
  const filteredGsuites = useMemo(
    () =>
      searchTerm
        ? gsuites.filter(({ displayName }) =>
            displayName
              .trim()
              .toLocaleLowerCase()
              .includes(searchTerm.trim().toLocaleLowerCase()),
          )
        : gsuites,
    [searchTerm, gsuites],
  )

  if (isError) {
    return <ErrorMessage />
  }

  return (
    <>
      <div className={cx("", className)} {...rest}>
        <div className="flex justify-between items-center flex-wrap sm:flex-nowrap gap-1">
          <h2 className="leading-8 text-2xl font-semibold">Admin Accounts</h2>
          <div className="flex gap-4 items-center">
            {selectedRowsKeys.length ? (
              <BinIcon
                onClick={() => setOpenDeleteModal(true)}
                className="mr-2 active:scale-90 w-6 h-6 cursor-pointer opacity-60 hover:text-primary hover:opacity-100"
              />
            ) : null}
            <TextField
              value={searchTerm}
              onChange={handleSearchTermChange}
              placeholder="Search account"
              startIcon={<SearchIcon className="w-6 h-6" />}
              className="text-sm outline outline-outline input-sm"
              rootClassName="basis-56"
              type="search"
            />
            <Button onClick={handleOpenAddModal} className="btn-primary btn-sm">
              <AddIcon className="w-6 h-6" />
              <span className="hidden sm:inline">Add Account</span>
            </Button>
          </div>
        </div>
        <div
          className={cx("mt-5 flex px-2 py-1 min-h-[292px]", {
            "justify-center items-center": !gsuites.length,
          })}
        >
          {isLoading ? (
            <Loader />
          ) : (
            <>
              {gsuites.length ? (
                <div className="w-full">
                  <GsuiteAccountsTable
                    gsuites={filteredGsuites}
                    page={skip}
                    total={total}
                    pageSize={limit}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    onRowSelect={(keys) => setSelectedRowsKeys(keys)}
                    loading={isFetching}
                  />
                </div>
              ) : (
                <EmptyList message="There are no admin accounts yet">
                  <Button
                    onClick={handleOpenAddModal}
                    className="btn-sm group bg-white hover:bg-primary border !border-solid border-outline-dark hover:border-primary"
                  >
                    <AddIcon className="transition-colors text-primary group-hover:text-white" />
                    <span className="transition-colors font-medium text-primary group-hover:text-white">
                      Add Account
                    </span>
                  </Button>
                </EmptyList>
              )}
            </>
          )}
        </div>
      </div>
      <AddEditGsuiteModal
        open={openAddModal}
        onClose={() => setOpenAddModal(false)}
      />
      <DeleteGsuiteModal
        data={selectedRowsKeys
          .map((key) => gsuites.find(({ _id }) => _id === key)!)
          .filter(Boolean)}
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
      />
    </>
  )
}

export default GsuitePage
